import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fmt } from '../helpers/helpers';
import moment from 'moment';

const ValueHistoryChart = ({ history }) => {
  // Filter and prepare chart data - only include value changes
  const valueHistory = history.filter(h =>
    h.value !== undefined && !h.isVirtual && h.event !== 'deleted'
  );

  // Check if we have multiple values to show
  if (valueHistory.length <= 1) {
    return null; // Don't show chart if only one value
  }

  // Transform history into chart data (chronological order)
  const chartData = valueHistory
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map(item => ({
      timestamp: item.timestamp,
      date: moment(item.timestamp).format('D/M HH:mm'),
      value: item.value, // Signed value: a sign flip (income <-> expense) must be visible
      originalValue: item.value
    }));

  // Zoom the Y-axis to the actual range so small variations are visible instead
  // of being squashed against a 0 baseline. Padding accounts for negative values.
  const values = chartData.map(d => d.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;
  const padding = range === 0 ? (Math.abs(maxValue) * 0.1 || 1) : range * 0.1;
  const yDomain = [minValue - padding, maxValue + padding];

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          background: 'white',
          border: '1px solid #ccc',
          padding: '8px 12px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
            {moment(data.timestamp).format('D/M/YYYY [às] HH:mm')}
          </p>
          <p style={{ margin: '4px 0 0 0', fontWeight: 'bold', color: '#333' }}>
            {fmt(data.originalValue)}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom axis tick for dates
  const CustomAxisTick = ({ x, y, payload }) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#666"
          fontSize="10"
          transform="rotate(-35)"
        >
          {payload.value}
        </text>
      </g>
    );
  };

  return (
    <div className="value-history-chart">
      <h4>📈 Evolução do Valor</h4>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart
          data={chartData}
          margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 60,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            height={60}
            tick={CustomAxisTick}
          />
          <YAxis
            width={60}
            domain={yDomain}
            tickFormatter={(value) => `${value}`}
          />
          <Tooltip content={CustomTooltip} />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#2196f3"
            strokeWidth={2}
            dot={{ fill: '#2196f3', r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ValueHistoryChart;
